import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useAccess, useAccountId } from "../Auth/auth-functions";
import { AzureADRoles } from "../Auth/auth-roles";
import { api } from "./api";
import { ListTeamDto, MutableTeamDto, TeamDto } from "../models/team.models";

export const useGetTeamsQuery = () => {
	const hasAccess = useAccess(AzureADRoles.All);
	const accountId = useAccountId();

	return useQuery({
		queryKey: ['teams', accountId],
		queryFn: async () => {
			let res = await api.get<ListTeamDto[]>(`/teams`);
			return res.data;
		},
		staleTime: 5 * 60 * 1000,
		gcTime: 5 * 60 * 1000,
		enabled: hasAccess
	});
}

export const useGetTeamByIdQuery = (teamId: string | null) => {
	const hasAccess = useAccess(AzureADRoles.All);
	const accountId = useAccountId();

	return useQuery({
		queryKey: ['team', accountId, teamId],
		queryFn: async () => {
			let res = await api.get<TeamDto>(`/teams/${teamId}`);
			return res.data;
		},
		staleTime: 60 * 1000,
		gcTime: 5 * 60 * 1000,
		enabled: hasAccess && teamId !== "" && teamId !== "new" && teamId !== null
	});
}

export const useCreateTeamMutation = () => {
	const queryClient = useQueryClient();
	const accountId = useAccountId();

	return useMutation<AxiosResponse<TeamDto>, Error, MutableTeamDto>({
		mutationFn: (model) => {
			return api.post('/teams', model);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['teams', accountId] });
		}
	});
}

export const useUpdateTeamMutation = () => {
	const queryClient = useQueryClient();
	const accountId = useAccountId();

	return useMutation<AxiosResponse<TeamDto>, Error, MutableTeamDto>({
		mutationFn: (model) => {
			return api.put(`/teams/${model.TeamId}`, model);
		},
		onSuccess: (_data, model) => {
			queryClient.invalidateQueries({ queryKey: ['team', accountId, model.TeamId] });
			queryClient.invalidateQueries({ queryKey: ['teams', accountId] });
		}
	});
}

export const useDeleteTeamMutation = () => {
	const queryClient = useQueryClient();
	const accountId = useAccountId();

	return useMutation<unknown, Error, TeamDto>({
		mutationFn: (team) => {
			return api.delete(`/teams/${team.TeamId}`);
		},
		onSuccess: (_data, team) => {
			queryClient.invalidateQueries({ queryKey: ['team', accountId, team.TeamId] });
			queryClient.invalidateQueries({ queryKey: ['teams', accountId] });
		}
	});
}

export const useRestoreTeamMutation = () => {
	const queryClient = useQueryClient();
	const accountId = useAccountId();

	return useMutation<unknown, Error, TeamDto>({
		mutationFn: (team) => {
			return api.post(`/teams/${team.TeamId}/restore`, team);
		},
		onSuccess: (_data, team) => {
			queryClient.invalidateQueries({ queryKey: ['team', accountId, team.TeamId] });
			queryClient.invalidateQueries({ queryKey: ['teams', accountId] });
		}
	});
}
